import { Palette, Theme } from "@mui/material/styles";
import { CSSProperties } from "@mui/styles/withStyles";
import { GetObjectKeysAsTemplateLiterals } from "@simplyk/common";

import { zeffyTheme } from "../../theme/theme";
import { ZeffyColorTokenBorder, ZeffyColorTokens, ZeffyColorTokenText } from "../../types/mui";

export type ZeffyColorTokenTextClasses = GetObjectKeysAsTemplateLiterals<Pick<ZeffyColorTokens, "text">>;
export type ZeffyColorTokenBorderClasses = GetObjectKeysAsTemplateLiterals<Pick<ZeffyColorTokens, "border">>;

/**
 * @deprecated This function is obsolete and should not be used in new code.
 * New components should use SX instead of className.
 */

export const generateZeffyColorTokenTextClasses = (
  theme: Theme,
  cssPalettePropName: "color" | "fill"
): Record<ZeffyColorTokenTextClasses, CSSProperties> =>
  Object.keys(theme.palette.text).reduce(
    (tokens: Record<ZeffyColorTokenTextClasses, CSSProperties>, token: string) => {
      if (typeof theme.palette.text[token as keyof ZeffyColorTokenText] === "string") {
        tokens[`text-${token}` as ZeffyColorTokenTextClasses] = {
          [cssPalettePropName]: theme.palette.text[token as keyof ZeffyColorTokenText] as string,
        };
        return tokens;
      }
      Object.keys(theme.palette.text[token as keyof ZeffyColorTokenText]).forEach((colorShade: string) => {
        tokens[`text-${token}-${colorShade}` as ZeffyColorTokenTextClasses] = {
          [cssPalettePropName]: (theme.palette.text[token as keyof ZeffyColorTokenText] as Record<string, string>)[
            colorShade
          ] as string,
        };
      });
      return tokens;
    },
    {} as Record<ZeffyColorTokenTextClasses, CSSProperties>
  );

export type PrefixedKey<Prefix extends string, Key extends string> = `${Prefix}-${Key}`;
export const getClassesKeyMap = <Prefix extends string, ClassKeys extends string>(
  prefix: Prefix,
  classes: Record<ClassKeys, CSSProperties>
) =>
  Object.keys(classes).reduce(
    (map, key) => {
      map[key as ClassKeys] = `${prefix}-${key as ClassKeys}`;
      return map;
    },
    {} as Record<ClassKeys, PrefixedKey<Prefix, ClassKeys>>
  );

export type InternalClasses<Prefix extends string, Classes extends string> = Record<
  Classes,
  PrefixedKey<Prefix, Classes>
>;

export const getZeffyColorClassesKeyMap = <Prefix extends string>(prefix: Prefix) => {
  return getClassesKeyMap(prefix, generateZeffyColorTokenTextClasses(zeffyTheme, "color"));
};

type StyleClassKey<Prefix extends string, ClassKey extends string> = `&.${PrefixedKey<Prefix, ClassKey>}`;
export const getRootStyleClassMap = <Prefix extends string, ClassKeys extends string>(
  prefix: Prefix,
  classes: Record<ClassKeys, CSSProperties>
) =>
  Object.keys(classes).reduce(
    (map, key) => {
      map[`&.${prefix}-${key as ClassKeys}`] = classes[key as ClassKeys];
      return map;
    },
    {} as Record<StyleClassKey<Prefix, ClassKeys>, CSSProperties>
  );

/**
 * @deprecated This function is obsolete and should not be used in new code.
 * New components should use SX instead of className.
 */
export const getZeffyColorTokenTextStyleMap = <Prefix extends string>(
  prefix: Prefix,
  theme: Theme,
  cssPalettePropName: "color" | "fill"
): Record<StyleClassKey<Prefix, ZeffyColorTokenTextClasses>, CSSProperties> => {
  const classes = generateZeffyColorTokenTextClasses(theme, cssPalettePropName);

  return getRootStyleClassMap(prefix, classes);
};

const BORDER_PREFIX = "border-";

export const generateZeffyBorderColorVibes = (theme: Theme): ZeffyColorTokenBorderClasses[] =>
  Object.keys(theme.palette.border)
    .map((token) =>
      typeof theme.palette.border[token as keyof ZeffyColorTokenBorder] === "string"
        ? `${BORDER_PREFIX}${token}`
        : Object.keys(theme.palette.border[token as keyof ZeffyColorTokenBorder]).map(
            (colorShade) => `${BORDER_PREFIX}${token}-${colorShade}` as ZeffyColorTokenBorderClasses
          )
    )
    .flat() as ZeffyColorTokenBorderClasses[];

const TEXT_PREFIX = "text-";

export const generateZeffyTextColorVibes = (theme: Theme): ZeffyColorTokenTextClasses[] =>
  Object.keys(theme.palette.text)
    .map((token) =>
      typeof theme.palette.text[token as keyof ZeffyColorTokenText] === "string"
        ? `${TEXT_PREFIX}${token}`
        : Object.keys(theme.palette.text[token as keyof ZeffyColorTokenText]).map(
            (colorShade) => `${TEXT_PREFIX}${token}-${colorShade}` as ZeffyColorTokenTextClasses
          )
    )
    .flat() as ZeffyColorTokenTextClasses[];

export const getNestedValueFromPalette = (record: Palette, path: string) => {
  const splitPathArray = path.split("-");

  return getNestedValueFromRecord(record as unknown as Record<string, unknown>, splitPathArray);
};

const getNestedValueFromRecord = (
  record: Record<string, unknown> | string,
  currentPath: string[]
): string | undefined => {
  if (typeof record !== "object" || currentPath.length === 0) {
    return record as string | undefined;
  }

  return getNestedValueFromRecord(
    record[currentPath[0]] as Record<string, unknown> | string,
    currentPath.length > 1 ? currentPath.splice(1) : []
  );
};
